import type { StyleHotProductCategoryProduct } from '../types';

export type StyleProductImageKind = 'product' | 'product_gray' | 'celebrity' | 'material' | 'other';

export interface StyleProductImageInfo {
  src: string;
  kind: StyleProductImageKind;
}

export function shouldCoverStyleProductImage(kind: StyleProductImageKind): boolean {
  return kind === 'celebrity' || kind === 'material';
}

interface PcStyleProductCardImages {
  productImage: StyleProductImageInfo;
  hoverProductImage: StyleProductImageInfo;
  shouldShowHoverProductImage: boolean;
}

const EMPTY_IMAGE: StyleProductImageInfo = {
  src: '',
  kind: 'other',
};

function getPrimaryImage(product: StyleHotProductCategoryProduct): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;
  const imageType = product.pc_image_type || 'product';
  const candidates: Array<[StyleProductImageKind, string[] | undefined]> = [
    [imageType as StyleProductImageKind, imageMap[imageType]],
    ['celebrity', imageMap.celebrity],
    ['material', imageMap.material],
    ['product', imageMap.product],
    ['product_gray', imageMap.product_gray],
  ];
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) {
    return EMPTY_IMAGE;
  }

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getDefaultHoverImage(product: StyleHotProductCategoryProduct): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;
  const candidates: Array<[StyleProductImageKind, string[] | undefined]> = [
    ['product_gray', imageMap.product_gray],
    ['product', imageMap.product],
  ];
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) {
    return EMPTY_IMAGE;
  }

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getFirstDistinctImage(
  kind: StyleProductImageKind,
  images: string[] | undefined,
  currentSrc: string,
  startIndex = 0,
): StyleProductImageInfo | null {
  const src = images?.find(
    (candidate, index) => index >= startIndex && Boolean(candidate) && candidate !== currentSrc,
  );

  return src ? { src, kind } : null;
}

function getProductGrayConfiguredHoverImage(
  product: StyleHotProductCategoryProduct,
  productImage: StyleProductImageInfo,
): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;

  switch (productImage.kind) {
    case 'product_gray':
      return (
        getFirstDistinctImage('material', imageMap.material, productImage.src) ??
        getFirstDistinctImage('celebrity', imageMap.celebrity, productImage.src) ??
        getFirstDistinctImage('product_gray', imageMap.product_gray, productImage.src, 1) ??
        getFirstDistinctImage('product', imageMap.product, productImage.src) ??
        EMPTY_IMAGE
      );

    case 'celebrity':
      return (
        getFirstDistinctImage('material', imageMap.material, productImage.src) ??
        getFirstDistinctImage('celebrity', imageMap.celebrity, productImage.src, 1) ??
        getFirstDistinctImage('product', imageMap.product, productImage.src) ??
        EMPTY_IMAGE
      );

    case 'material':
      return (
        getFirstDistinctImage('material', imageMap.material, productImage.src, 1) ??
        getFirstDistinctImage('celebrity', imageMap.celebrity, productImage.src) ??
        getFirstDistinctImage('product', imageMap.product, productImage.src) ??
        EMPTY_IMAGE
      );

    case 'product':
      return (
        getFirstDistinctImage('material', imageMap.material, productImage.src) ??
        getFirstDistinctImage('celebrity', imageMap.celebrity, productImage.src) ??
        getFirstDistinctImage('product', imageMap.product, productImage.src, 1) ??
        EMPTY_IMAGE
      );

    default:
      return EMPTY_IMAGE;
  }
}

export function getPcStyleProductCardImages(
  product: StyleHotProductCategoryProduct,
): PcStyleProductCardImages {
  const productImage = getPrimaryImage(product);
  const isProductGrayConfigured = product.pc_image_type === 'product_gray';
  const hoverProductImage = isProductGrayConfigured
    ? getProductGrayConfiguredHoverImage(product, productImage)
    : getDefaultHoverImage(product);
  const shouldShowHoverProductImage =
    Boolean(hoverProductImage.src) &&
    (isProductGrayConfigured ||
      productImage.kind === 'celebrity' ||
      productImage.kind === 'material');

  return {
    productImage,
    hoverProductImage,
    shouldShowHoverProductImage,
  };
}
